import React, { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea
} from '../ui'
import HabilidadesSelector from './HabilidadesSelector'
import { useAuth } from '../../hooks/useAuth'
import { useOrganization } from '../../contexts/OrganizationContext'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'
import { createSeccionTemplate, updateSeccionTemplate, getOrgDeportes } from '../../lib/supabase'
import { useDeportes } from '../../hooks/useDeportes'
import { opcionesDeporte } from '../../lib/deportesOrg'
import { SECCION_TIPOS } from '../../constants/seccionTipos'

const PlantillaSeccionModal = ({ open, onOpenChange, plantilla = null, onSaved }) => {
  const { staff } = useAuth()
  const { org } = useOrganization()
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })
  const isEditing = Boolean(plantilla)

  const [nombre, setNombre] = useState('')
  const [duracionMin, setDuracionMin] = useState(5)
  const [duracionSeg, setDuracionSeg] = useState(0)
  const [descripcion, setDescripcion] = useState('')
  const [tipo, setTipo] = useState('otro')
  const [deporte, setDeporte] = useState('todos')
  const [habilidadIds, setHabilidadIds] = useState([])
  const [orgDeportes, setOrgDeportes] = useState([])
  const { label: nombreDeporte } = useDeportes()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || !org?.id) return
    let cancelled = false
    getOrgDeportes(org.id).then(({ data }) => {
      if (!cancelled) setOrgDeportes(data || [])
    })
    return () => { cancelled = true }
  }, [open, org?.id])

  useEffect(() => {
    if (!open) return
    if (plantilla) {
      setNombre(plantilla.nombre || '')
      setDuracionMin(Math.floor((plantilla.duracion_seg || 0) / 60))
      setDuracionSeg((plantilla.duracion_seg || 0) % 60)
      setDescripcion(plantilla.descripcion || '')
      setTipo(plantilla.tipo || 'otro')
      setDeporte(plantilla.deporte || 'todos')
      setHabilidadIds(plantilla.habilidad_ids || [])
    } else {
      setNombre('')
      setDuracionMin(5)
      setDuracionSeg(0)
      setDescripcion('')
      setTipo('otro')
      setDeporte('todos')
      setHabilidadIds([])
    }
    setError(null)
    guard.resetDirty()
  }, [open, plantilla]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleGuardar = async () => {
    const nombreLimpio = nombre.trim()
    if (nombreLimpio.length < 3) {
      setError('El nombre debe tener al menos 3 caracteres')
      return
    }
    const totalSeg = (parseInt(duracionMin, 10) || 0) * 60 + (parseInt(duracionSeg, 10) || 0)
    if (totalSeg < 30) {
      setError('La duracion minima es 30 segundos')
      return
    }
    if (!org?.id) {
      setError('No pudimos identificar la organizacion')
      return
    }

    setSaving(true)
    setError(null)
    try {
      if (isEditing) {
        const { error: err } = await updateSeccionTemplate(plantilla.id, {
          nombre: nombreLimpio,
          duracion_seg: totalSeg,
          descripcion: descripcion.trim() || null,
          tipo,
          deporte,
          habilidadIds
        })
        if (err) throw err
      } else {
        const { error: err } = await createSeccionTemplate({
          org_id: org.id,
          nombre: nombreLimpio,
          duracion_seg: totalSeg,
          descripcion: descripcion.trim() || null,
          tipo,
          deporte,
          habilidadIds,
          created_by: staff?.id || null
        })
        if (err) throw err
      }
      guard.resetDirty()
      onOpenChange(false)
      if (onSaved) onSaved()
    } catch (err) {
      setError(err.message || 'No pudimos guardar la plantilla')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar plantilla' : 'Nueva plantilla de seccion'}
          </DialogTitle>
          <DialogDescription>
            Estas plantillas se pueden importar luego al crear cualquier programa.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="nombre">Nombre</Label>
            <Input
              id="nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Flex Dinamico, Hidratacion, Quick Feet..."
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Minutos</Label>
              <Input
                type="number"
                min="0"
                value={duracionMin}
                onChange={(e) => setDuracionMin(e.target.value)}
              />
            </div>
            <div>
              <Label>Segundos</Label>
              <Input
                type="number"
                min="0"
                max="59"
                value={duracionSeg}
                onChange={(e) => setDuracionSeg(e.target.value)}
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SECCION_TIPOS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="descripcion">Descripción (opcional)</Label>
            <Textarea
              id="descripcion"
              rows={3}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Detalle del ejercicio, indicaciones, objetivo..."
            />
          </div>

          <div>
            <Label>Deporte</Label>
            <Select
              value={deporte}
              onValueChange={(v) => { guard.markDirty(); setDeporte(v); setHabilidadIds([]) }}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">General (cualquier deporte)</SelectItem>
                {opcionesDeporte(orgDeportes, deporte === 'todos' ? null : deporte, nombreDeporte).map((o) => (
                  <SelectItem key={o.value} value={o.value} disabled={o.deshabilitado}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              "General" = calentamiento, hidratacion, fisico, cierre: sirve para cualquier deporte.
            </p>
          </div>

          <HabilidadesSelector
            deportes={deporte === 'todos' ? orgDeportes.map((od) => od.deporte_id) : [deporte]}
            selectedIds={habilidadIds}
            onChange={(ids) => { guard.markDirty(); setHabilidadIds(ids) }}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => guard.onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {isEditing ? 'Guardar cambios' : 'Crear plantilla'}
          </Button>
        </DialogFooter>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default PlantillaSeccionModal
